// User-facing reference for everything you can do in this tool.
// Organised top-down by workflow: get started, dictate, dictate at scale,
// edit/correct, review, export, settings. Designed to be skimmable —
// every section has a one-line summary plus a small example block.

export function UserGuide() {
  return (
    <div className="guide">
      <h2>Quick guide</h2>
      <p className="guide-lede">
        NVH Dictation Tool turns spoken time entries into a tidy timesheet.
        Hit the mic, talk, get rows. Everything below is optional — you
        can always type entries directly or edit cells in the grid.
      </p>

      {/* ----- 1. Getting started ----- */}
      <section>
        <h3>1. Start a session</h3>
        <p>
          Sessions group your entries — typically one per case or per day.
          Use the <strong>Sessions</strong> sidebar on the left to create,
          rename (✎), or delete (🗑) them. Click any session to open it.
        </p>
      </section>

      <section>
        <h3>2. Set defaults at the top of the session</h3>
        <p>
          The banner across the top of each session shows{' '}
          <strong>Default date</strong> and <strong>Default fee earner</strong>.
          Click either to set it. Any row you dictate without saying its
          own date / fee earner inherits these. The moment you dictate a
          row that uses a <em>different</em> date or fee earner, the
          relevant default resets to "not set" — no risk of stale defaults
          quietly attaching to subsequent rows.
        </p>
      </section>

      {/* ----- 2. Dictation ----- */}
      <section>
        <h3>3. Dictate entries (the fast way)</h3>
        <p>
          Click the record button (or press <kbd>Space</kbd>) to start.
          Talk naturally; pause for ~6 seconds to send a batch to the
          transcription model; carry on talking. Click record / press
          <kbd>Space</kbd> again when you're done. Rows land in the grid
          as each batch is transcribed.
        </p>
        <p>
          Every row needs a <strong>time expression at the end</strong>:
        </p>
        <pre>{`review Defence two units
email client re disclosure 1 unit
draft chronology 3 units
attendance on counsel four units
consider Part 36 offer 0.2
attendance at conference 1.2 hours
telephone attendance 48 minutes`}</pre>
        <p className="muted">
          Recognised forms: whole numbers with <code>units</code>{' '}
          (<code>two units</code>, <code>12 units</code>); decimals with{' '}
          <code>hours</code> (<code>1.2 hours</code>,{' '}
          <code>one point five hours</code>) or bare (<code>0.2</code>,{' '}
          <code>point five</code>); whole minutes with{' '}
          <code>minutes</code> (<code>48 minutes</code> = 8 units —
          partial units round <em>up</em>, so <code>20 minutes</code> = 4
          units). Units are whole numbers; decimals belong with hours — a
          spoken <code>1.2 units</code> is read charitably as 1.2 hours.
          A bare number with no <em>units</em> / <em>hours</em> /{' '}
          <em>minutes</em> / <em>point</em> is <strong>not</strong>{' '}
          treated as time (too easy to confuse with page numbers, version
          numbers, etc.).
        </p>
      </section>

      <section>
        <h3>4. Dictate the date and name inline</h3>
        <p>
          You can carry the date and fee earner into a single utterance,
          comma- or pause-separated, and the parser will split it up:
        </p>
        <pre>{`8 May 2026, Ruth Winter, reviewing medical records, 18 units
8th of May 2026, Ruth Winter, drafting particulars, 5 units`}</pre>
        <p className="muted">
          For a name to be auto-recognised, configure it under{' '}
          <strong>Settings → Fee earners</strong> with the spoken form
          in the Aliases field (e.g. code <code>RW</code>, alias{' '}
          <code>Ruth Winter</code>). Without that the name stays in the
          description, the date still extracts cleanly.
        </p>
      </section>

      <section>
        <h3>5. Dictate many rows back-to-back</h3>
        <p>
          You don't need to click stop between rows. Speak the whole
          batch with brief gaps; the parser splits at every "X units" /
          "point X" boundary. Pause for ~6 seconds when you're really
          done with that batch — it ships, the rows land in the grid,
          and recording carries on for the next batch.
        </p>
        <pre>{`review pleadings 3 units. email opponent 1 unit. attend client 5 units.
draft witness statement 8 units.`}</pre>
      </section>

      {/* ----- 3. Verbal context + correction commands ----- */}
      <section>
        <h3>6. Verbal context commands</h3>
        <p>
          Spoken commands that <em>set</em> defaults (rather than create
          a row) — useful when a date or fee earner applies to many rows
          in a row:
        </p>
        <pre>{`date 12 March 2024
date 13/03/2024
date twelve March twenty twenty four
fee earner JH
fee earner Ruth Winter
section pleadings
reference page 142`}</pre>
        <p className="muted">
          "fee owner" is also accepted (speech-to-text often mis-hears
          "fee earner"). "source" is a synonym for "section".
        </p>
      </section>

      <section>
        <h3>7. Verbal correction commands</h3>
        <p>If the last row you dictated needs fixing, you can say:</p>
        <pre>{`void last         (or "undo last" / "delete last")
restore last      (un-void the last voided row)
check last        (mark the last row as reviewed)
uncheck last
amend last time to four units
amend last description to draft witness statement
last time 0.5     (shorthand for "amend last time")
replace last review of medical records 5 units
ignore that       (discard the last unprocessed utterance)`}</pre>
      </section>

      {/* ----- 4. Editing in the grid ----- */}
      <section>
        <h3>8. Edit, reorder, delete in the grid</h3>
        <p>Anything dictation gets wrong, you can fix without re-recording:</p>
        <ul>
          <li><strong>Double-click any cell</strong> to edit it inline. Enter or click away to commit, Escape to cancel.</li>
          <li><strong>▲ ▼ buttons</strong> on each row reorder it.</li>
          <li><strong>🗑 button</strong> deletes the row (with confirmation).</li>
          <li><strong>+ Add row</strong> below the grid opens a manual entry form — useful when you have an entry that pre-dates the dictation session.</li>
          <li><strong>Void / restore last</strong> in the toolbar treats the most recent row only.</li>
        </ul>
      </section>

      <section>
        <h3>9. Review queue</h3>
        <p>
          Anything the parser couldn't make sense of (an utterance with
          no time expression, a hallucinated noise sample, etc.) lands
          in the review queue. Open it from the toolbar (or{' '}
          <kbd>Ctrl/Cmd</kbd>+<kbd>R</kbd>) to inspect, edit, or
          discard. You can also filter the main grid to{' '}
          <em>With warnings</em> to triage rows the parser accepted but
          flagged.
        </p>
      </section>

      {/* ----- 5. Output ----- */}
      <section>
        <h3>10. Export</h3>
        <p>
          The toolbar's <strong>Export…</strong> button produces a clean
          XLSX of the active session. <strong>More exports…</strong>{' '}
          offers other formats:
        </p>
        <ul>
          <li><strong>Clean</strong> — voided rows excluded, ready for the bill.</li>
          <li><strong>Audit</strong> — every row including voided ones, plus the original transcript and any manual edit flags. Use this to evidence the file.</li>
          <li><strong>Review</strong> — only the rows the parser couldn't fully assign; useful as a fixup worksheet.</li>
        </ul>
        <p>Each available in CSV or XLSX.</p>
      </section>

      {/* ----- 6. Settings + tips ----- */}
      <section>
        <h3>11. Settings worth setting once</h3>
        <ul>
          <li>
            <strong>Fee earners</strong> — code + comma-separated aliases
            (e.g. <code>RW</code> with aliases{' '}
            <code>Ruth Winter, Ruth, RW</code>). Better recognition,
            cleaner exports.
          </li>
          <li>
            <strong>Cloud privacy acknowledged</strong> — record-of-decision
            for using cloud transcription. Required to enable the mic.
          </li>
          <li>
            <strong>High-unit warning threshold</strong> — flags any row
            that exceeds it so you can spot stray "20 units" mishears.
          </li>
          <li>
            <strong>Export date format</strong> — DD/MM/YYYY by default.
          </li>
        </ul>
      </section>

      <section>
        <h3>12. Keyboard shortcuts</h3>
        <ul>
          <li><kbd>Space</kbd> — start / stop the mic (when no input is focused).</li>
          <li><kbd>Enter</kbd> in the transcript box — submit the line.</li>
          <li><kbd>Shift</kbd>+<kbd>Enter</kbd> in the transcript box — newline (paste several entries at once).</li>
          <li><kbd>Ctrl/Cmd</kbd>+<kbd>Z</kbd> — void the last row.</li>
          <li><kbd>Ctrl/Cmd</kbd>+<kbd>R</kbd> — open the review queue.</li>
          <li><kbd>Ctrl/Cmd</kbd>+<kbd>E</kbd> — quick export (clean XLSX).</li>
          <li><kbd>Ctrl/Cmd</kbd>+<kbd>F</kbd> — focus the transcript box.</li>
        </ul>
      </section>

      <section>
        <h3>13. If something looks off</h3>
        <ul>
          <li>
            <strong>Mic level shows 0%</strong> while you speak →
            browser/OS isn't delivering audio. Check System Settings →
            Privacy & Security → Microphone. The "Test mic (5s)" button
            isolates this from the rest of the app.
          </li>
          <li>
            <strong>Whisper mishears a name</strong> → add the spoken
            form to that fee earner's Aliases in Settings. Subsequent
            batches use it automatically.
          </li>
          <li>
            <strong>Row landed in the wrong place</strong> → ▲ ▼ on the
            row, or just edit cells. No need to void / re-record.
          </li>
        </ul>
      </section>
    </div>
  );
}
